import React, { useCallback, useState } from "react";
import {
  DollarSign,
  ShoppingCart,
  Package,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Star,
  MessageSquare,
  Gift,
  Wallet,
  Bell,
  Send,
  Eye,
  MoreVertical,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  BarChart3,
  ShoppingBag,
  Tag,
  Receipt,
  Landmark,
  ShieldCheck,
} from "lucide-react";
import * as S from "./dashboardStyle";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { Store } from "@apis/index";
import { useEffect } from "react";
import Typography from "@components/typography";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

import StoreOverviewSkeleton from "./StoreOverviewSkeleton";

interface StoreOverviewDashboardProps {
  storeId: number;
}

const StoreOverviewDashboard: React.FC<StoreOverviewDashboardProps> = ({
  storeId,
}) => {
  const { data: singleStore } = useSelector(
    (state: RootState) => state.singleStore,
  );
  const findStore = useCallback(async () => {
    if (!storeId) return;
    await Store.find({ state: "singleStore", id: storeId, showErr: false });
  }, [storeId]);
  useEffect(() => {
    findStore();
  }, [findStore]);
  const { settings } = useGeneralSettings();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("Total");

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!storeId) return;
      try {
        setLoading(true);
        const res = await Store.getStoreAnalytics(storeId);
        if (res) {
          setAnalyticsData(res);
        }
      } catch (error) {
        // console.error("Failed to fetch store analytics", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [storeId]);

  if (loading) return <StoreOverviewSkeleton />;
  if (!analyticsData) return <div>No analytics data available</div>;

  const {
    earnings,
    orderOverview,
    productOverview,
    transactionOverview,
    priceBreakdown,
    moduleCommission,
    withdrawalOverview,
    recentWithdrawals,
  } = analyticsData;

  const transactionData = {
    totalTransactions: transactionOverview.totalSales > 0 ? 1 : 0,
    successfulTransactions: orderOverview.Delivered || 0,
    failedTransactions: orderOverview.Cancelled || 0,
    pendingTransactions: orderOverview["Order Placed"] || 0,
  };

  const recentOrders: any[] = [];
  const recentTransactions: any[] = [];
  const activeDealsCount = analyticsData.activeDeals || 0;
  const recentReviews = analyticsData.recentReviews || [];
  const withdrawalRequests: any[] = [];
  const payoutHistory: any[] = [];
  const paymentDetails: any = {
    bankName: "N/A",
    accountNumber: "N/A",
    accountHolder: "N/A",
    routingNumber: "N/A",
    paymentMethod: "N/A",
    nextPayoutDate: "N/A",
    availableBalance: 0,
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "completed":
      case "success":
      case "active":
      case "approved":
        return "#10b981";
      case "pending":
      case "order placed":
        return "#f59e0b";
      case "processing":
      case "order processing":
      case "delivery assigned":
        return "#3b82f6";
      case "cancelled":
      case "failed":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
      case "approved":
      case "delivered":
        return <CheckCircle size={16} />;
      case "pending":
      case "order placed":
        return <Clock size={16} />;
      case "processing":
      case "order processing":
      case "delivery assigned":
        return <AlertCircle size={16} />;
      case "cancelled":
      case "failed":
        return <XCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getPeriodData = () => {
    if (!analyticsData?.orderOverview) return {};
    switch (selectedPeriod) {
      case "Today":
        return analyticsData.orderOverview.today || {};
      case "This Week":
        return analyticsData.orderOverview.thisWeek || {};
      case "This Month":
        return analyticsData.orderOverview.thisMonth || {};
      case "This Year":
        return analyticsData.orderOverview.thisYear || {};
      default:
        return analyticsData.orderOverview;
    }
  };

  const currentOrderData = getPeriodData();

  return (
    <S.DashboardContainer>
      {/* Financial Breakdown Section */}
      {priceBreakdown && (
        <S.Section>
          <S.SectionTitle>
            <BarChart3 size={20} />
            Financial Summary
          </S.SectionTitle>

          <div style={{ marginBottom: "32px" }}>
            <Typography
              size={12}
              weight={700}
              color="#64748b"
              styleText="text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px; display: flex; align-items: center; gap: 8px;"
            >
              <TrendingUp size={14} />
              Order Revenue Flow
            </Typography>
            <S.BreakdownGrid>
              <S.BreakdownCard>
                <S.IconContainer color="#64748b">
                  <ShoppingBag size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Product Total (MRP)</S.BreakdownLabel>
                <S.BreakdownValue>
                  {Helper.formatCurrency(
                    priceBreakdown.originalAmount,
                    settings,
                  )}
                </S.BreakdownValue>
              </S.BreakdownCard>

              <S.BreakdownCard>
                <S.IconContainer color="#10b981">
                  <Tag size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Discounts & Coupons</S.BreakdownLabel>
                <S.BreakdownValue color="#10b981">
                  - {Helper.formatCurrency(priceBreakdown.discount, settings)}
                </S.BreakdownValue>
                <S.BreakdownSubtext>
                  Includes{" "}
                  {Helper.formatCurrency(
                    priceBreakdown.couponDiscount,
                    settings,
                  )}{" "}
                  Admin Coupons
                </S.BreakdownSubtext>
              </S.BreakdownCard>

              <S.BreakdownCard>
                <S.IconContainer color="#ef4444">
                  <Receipt size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Commission & Fees</S.BreakdownLabel>
                <S.BreakdownValue color="#ef4444">
                  -{" "}
                  {Helper.formatCurrency(
                    priceBreakdown.commissionCharge,
                    settings,
                  )}
                </S.BreakdownValue>
                <S.BreakdownSubtext>
                  Admin rate:{" "}
                  {moduleCommission?.type === "flat"
                    ? Helper.formatCurrency(moduleCommission?.value || 0, settings)
                    : `${moduleCommission?.value || 0}%`}
                  {moduleCommission?.moduleName
                    ? ` (${moduleCommission.moduleName})`
                    : ""}
                </S.BreakdownSubtext>
              </S.BreakdownCard>

              <S.BreakdownCard highlight>
                <S.IconContainer color="#ffffff" highlight>
                  <TrendingUp size={16} />
                </S.IconContainer>
                <S.BreakdownLabel inverted>
                  Total Customer Flow
                </S.BreakdownLabel>
                <S.BreakdownValue inverted>
                  {Helper.formatCurrency(
                    priceBreakdown.customerTotal,
                    settings,
                  )}
                </S.BreakdownValue>
              </S.BreakdownCard>
            </S.BreakdownGrid>
          </div>

          <div>
            <Typography
              size={12}
              weight={700}
              color="#64748b"
              styleText="text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px; display: flex; align-items: center; gap: 8px;"
            >
              <Landmark size={14} />
              Wallet & Settlements
            </Typography>
            <S.BreakdownGrid>
              <S.BreakdownCard highlight>
                <S.IconContainer color="#ffffff" highlight>
                  <Wallet size={16} />
                </S.IconContainer>
                <S.BreakdownLabel inverted>
                  Total Wallet Amount
                </S.BreakdownLabel>
                <S.BreakdownValue inverted>
                  {Helper.formatCurrency(singleStore.walletBalance, settings)}
                </S.BreakdownValue>
              </S.BreakdownCard>

              <S.BreakdownCard>
                <S.IconContainer color="#f59e0b">
                  <Clock size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Pending Withdrawal</S.BreakdownLabel>
                <S.BreakdownValue color="#f59e0b">
                  {Helper.formatCurrency(
                    withdrawalOverview?.Pending || 0,
                    settings,
                  )}
                </S.BreakdownValue>
              </S.BreakdownCard>

              <S.BreakdownCard>
                <S.IconContainer color="#10b981">
                  <ShieldCheck size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Successful Withdrawal</S.BreakdownLabel>
                <S.BreakdownValue color="#10b981">
                  {Helper.formatCurrency(
                    withdrawalOverview?.Approved || 0,
                    settings,
                  )}
                </S.BreakdownValue>
              </S.BreakdownCard>

              <S.BreakdownCard>
                <S.IconContainer color="#ef4444">
                  <XCircle size={16} />
                </S.IconContainer>
                <S.BreakdownLabel>Rejected Withdrawal</S.BreakdownLabel>
                <S.BreakdownValue color="#ef4444">
                  {Helper.formatCurrency(
                    withdrawalOverview?.Rejected || 0,
                    settings,
                  )}
                </S.BreakdownValue>
              </S.BreakdownCard>
            </S.BreakdownGrid>
          </div>

          <S.BreakdownFooter>
            <Typography size={13} color="#64748b">
              * This summary aggregates data from all **Delivered** orders for
              this store.
            </Typography>
          </S.BreakdownFooter>
        </S.Section>
      )}

      {/* Order & Product Overview */}
      <S.TwoColumnGrid>
        <S.Section>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <S.SectionTitle style={{ margin: 0 }}>
              <ShoppingCart size={20} />
              Order Overview
            </S.SectionTitle>
            <S.FormSelect
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              style={{
                padding: "8px 12px",
                fontSize: "12px",
                borderRadius: "8px",
              }}
            >
              <option value="Total">Lifetime</option>
              <option value="Today">Today</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="This Year">This Year</option>
            </S.FormSelect>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <S.StatItem>
              <S.StatLabel>Total</S.StatLabel>
              <S.StatValue size="20px">
                {currentOrderData.totalOrders || 0}
              </S.StatValue>
            </S.StatItem>
            <S.StatItem>
              <S.StatLabel>Completed</S.StatLabel>
              <S.StatValue size="20px" color="#10b981">
                {currentOrderData.totalCompletedOrders || 0}
              </S.StatValue>
            </S.StatItem>
            <S.StatItem>
              <S.StatLabel>Ongoing</S.StatLabel>
              <S.StatValue size="20px" color="#f59e0b">
                {currentOrderData.totalOngoingOrders || 0}
              </S.StatValue>
            </S.StatItem>
          </div>

          <Typography
            size={11}
            weight={700}
            color="#94a3b8"
            styleText="text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; display: block;"
          >
            Status Breakdown
          </Typography>

          <S.StatsGrid>
            {Object.entries(currentOrderData)
              .filter(
                ([key, count]) =>
                  typeof count === "number" && !key.startsWith("total"),
              )
              .map(([status, count]: any) => (
                <S.StatItem key={status}>
                  <S.StatLabel>{status}</S.StatLabel>
                  <S.StatValue color={getStatusColor(status)}>
                    {count}
                  </S.StatValue>
                </S.StatItem>
              ))}
          </S.StatsGrid>
        </S.Section>

        <S.Section>
          <S.SectionTitle>
            <Package size={20} />
            Product Overview
          </S.SectionTitle>
          <S.StatsGrid>
            <S.StatItem>
              <S.StatLabel>Total Products</S.StatLabel>
              <S.StatValue>{productOverview.total}</S.StatValue>
            </S.StatItem>
            <S.StatItem>
              <S.StatLabel>Active</S.StatLabel>
              <S.StatValue color="#10b981">
                {productOverview.active}
              </S.StatValue>
            </S.StatItem>
            <S.StatItem>
              <S.StatLabel>Inactive</S.StatLabel>
              <S.StatValue color="#ef4444">
                {productOverview.inactive}
              </S.StatValue>
            </S.StatItem>
          </S.StatsGrid>
        </S.Section>
      </S.TwoColumnGrid>

      {/* Withdrawal Overview */}
      <S.Section>
        <S.SectionTitle>
          <Wallet size={20} />
          Withdrawal History
        </S.SectionTitle>
        <S.TableContainer>
          <S.Table>
            <S.TableHeader>
              <S.TableRow>
                <S.TableHead>Amount</S.TableHead>
                <S.TableHead>Status</S.TableHead>
                <S.TableHead>Date</S.TableHead>
                <S.TableHead>Remarks</S.TableHead>
              </S.TableRow>
            </S.TableHeader>
            <S.TableBody>
              {recentWithdrawals && recentWithdrawals.length > 0 ? (
                recentWithdrawals.map((item: any) => (
                  <S.TableRow key={item.id}>
                    <S.TableCell>
                      <S.AmountText positive={false}>
                        {Helper.formatCurrency(item.amount, settings)}
                      </S.AmountText>
                    </S.TableCell>
                    <S.TableCell>
                      {(() => {
                        const isPartiallyPaid =
                          item.status === "Pending" &&
                          (item.paidAmount || 0) > 0;
                        const isPartiallyPaidAndRejected =
                          item.status === "Rejected" &&
                          (item.paidAmount || 0) > 0;

                        let displayStatus = item.status;
                        let colorStatus = item.status;

                        if (isPartiallyPaid) {
                          displayStatus = "Partially Paid";
                          colorStatus = "Approved";
                        } else if (isPartiallyPaidAndRejected) {
                          displayStatus = "Partially Paid & Rejected";
                          colorStatus = "Rejected";
                        }

                        return (
                          <S.StatusBadge color={getStatusColor(colorStatus)}>
                            {getStatusIcon(colorStatus)}
                            {displayStatus}
                          </S.StatusBadge>
                        );
                      })()}
                    </S.TableCell>
                    <S.TableCell>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </S.TableCell>
                    <S.TableCell>
                      <div
                        style={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography size={12} color="#64748b">
                          {item.adminNote || "—"}
                        </Typography>
                      </div>
                    </S.TableCell>
                  </S.TableRow>
                ))
              ) : (
                <S.TableRow>
                  <S.TableCell colSpan={4} style={{ textAlign: "center" }}>
                    No withdrawal history found
                  </S.TableCell>
                </S.TableRow>
              )}
            </S.TableBody>
          </S.Table>
        </S.TableContainer>
      </S.Section>

      {/* Deals & Reviews Section */}
      <S.TwoColumnGrid>
        <S.Section>
          <S.SectionTitle>
            <Gift size={20} />
            Active Deals
          </S.SectionTitle>
          <S.StatsGrid>
            <S.StatItem>
              <S.StatLabel>Total Active Deals</S.StatLabel>
              <S.StatValue color="#3b82f6">{activeDealsCount}</S.StatValue>
            </S.StatItem>
          </S.StatsGrid>
        </S.Section>

        <S.Section>
          <S.SectionTitle>
            <Star size={20} />
            Recent Reviews
          </S.SectionTitle>
          <S.ReviewsList>
            {recentReviews.length > 0 ? (
              recentReviews.map((review: any) => (
                <S.ReviewCard key={review.id}>
                  <S.ReviewHeader>
                    <S.ReviewCustomer>{review.customerName}</S.ReviewCustomer>
                    <S.ReviewRating>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={12}
                          fill={i < review.rating ? "#f59e0b" : "transparent"}
                          color={i < review.rating ? "#f59e0b" : "#cbd5e1"}
                        />
                      ))}
                    </S.ReviewRating>
                  </S.ReviewHeader>
                  <S.ReviewComment>{review.comment}</S.ReviewComment>
                  <S.ReviewProduct>
                    {new Date(review.createdAt).toLocaleDateString()}
                  </S.ReviewProduct>
                </S.ReviewCard>
              ))
            ) : (
              <div
                style={{
                  padding: "20px",
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                No recent reviews found
              </div>
            )}
          </S.ReviewsList>
        </S.Section>
      </S.TwoColumnGrid>

      {/* Transaction Overview */}
      <S.Section>
        <S.SectionTitle>
          <CreditCard size={20} />
          Transaction Overview
        </S.SectionTitle>
        <S.MetricsGrid>
          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#6366f1">
                <CreditCard size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>{transactionData.totalTransactions}</S.MetricValue>
            <S.MetricLabel>Total Transactions</S.MetricLabel>
          </S.MetricCard>

          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#10b981">
                <CheckCircle size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>
              {transactionData.successfulTransactions}
            </S.MetricValue>
            <S.MetricLabel>Successful</S.MetricLabel>
          </S.MetricCard>

          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#f59e0b">
                <Clock size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>{transactionData.pendingTransactions}</S.MetricValue>
            <S.MetricLabel>Pending</S.MetricLabel>
          </S.MetricCard>

          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#ef4444">
                <XCircle size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>{transactionData.failedTransactions}</S.MetricValue>
            <S.MetricLabel>Failed</S.MetricLabel>
          </S.MetricCard>
        </S.MetricsGrid>
      </S.Section>
    </S.DashboardContainer>
  );
};

export default StoreOverviewDashboard;
